import React from 'react';
import { cn } from '~/lib/utils';
import styles from './OrderedList.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface OrderedListProps {
  /**
   * One node per step, in order. Nodes rather than strings because a step may carry an inline
   * `MentionChip` or a code run; the marker is derived from position and is never passed in.
   */
  items: React.ReactNode[];
  theme?: ChatTheme;
  className?: string;
}

/**
 * The numbered steps of a structured assistant answer (the *Rich answer* artboard — "a heading, a
 * table and numbered steps. prose was only ever paragraphs.").
 *
 * A real `<ol>`/`<li>` so the ordinal reaches assistive tech, with the UA's own marker layout
 * turned off and the numeral redrawn in a fixed 16px slot. The visible "1." is therefore a
 * duplicate of information the list role already carries, so it is `aria-hidden` — a screen reader
 * that announced "one, one. clamp at eight lines" would be reading the scaffolding aloud.
 *
 * The list starts at 1 and there is no `start` prop: nothing in eighty artboards continues a list
 * across a block, and a marker that could disagree with the DOM order is a bug waiting to be
 * filed.
 */
export function OrderedList({ items, theme: themeProp, className }: OrderedListProps) {
  const theme = useV15Theme(themeProp);
  return (
    <ol className={cn(chatThemeClass(theme), styles.root, className)}>
      {items.map((item, index) => (
        // Index is the key because the marker IS the index: these rows are a rendered answer, not
        // a mutable collection — they never reorder, and nothing in them holds state.
        <li key={index} className={styles.item}>
          <span className={styles.marker} aria-hidden="true">
            {index + 1}.
          </span>
          <span className={styles.body}>{item}</span>
        </li>
      ))}
    </ol>
  );
}
